import {  useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Check, Link, Loader2, Mail, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type {KeyboardEvent} from 'react';
import { validateEmailDomain } from '@/lib/api/features/workspace-invites/schemas/inviteFormSchema'
import { createInvite } from '@/lib/api/features/workspace-invites/api/createInvite'
import { getInviteLink } from '@/lib/api/features/workspace-invites/api/getInviteLink'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  workspaceSlug: string
  workspaceName: string
  onInviteSent?: () => void
}

type ModalView = 'link' | 'email'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function InviteMemberModal({
  isOpen,
  onClose,
  workspaceSlug,
  workspaceName,
  onInviteSent,
}: InviteMemberModalProps) {
  const { t } = useTranslation('settings')
  const [view, setView] = useState<ModalView>('email')

  // Link view state
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [loadingLink, setLoadingLink] = useState(false)
  const [copied, setCopied] = useState(false)

  // Email view state
  const [emails, setEmails] = useState<Array<string>>([])
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchInviteLink = useCallback(async () => {
    setLoadingLink(true)
    try {
      const result = await getInviteLink(workspaceSlug)
      setInviteUrl(result.inviteUrl)
    } catch {
      toast.error(t('members.invites.modal.linkLoadFailed'))
    } finally {
      setLoadingLink(false)
    }
  }, [workspaceSlug, t])

  useEffect(() => {
    if (isOpen && !inviteUrl) {
      fetchInviteLink()
    }
  }, [isOpen, inviteUrl, fetchInviteLink])

  const handleCopyLink = async () => {
    if (inviteUrl) {
      await navigator.clipboard.writeText(inviteUrl)
      setCopied(true)
      toast.success(t('members.invites.modal.linkCopied'))
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const addEmail = useCallback((raw: string) => {
    const email = raw.trim().toLowerCase()
    if (!email) return false

    if (!EMAIL_REGEX.test(email)) {
      setEmailError(t('members.invites.modal.errors.invalid'))
      return false
    }

    if (emails.includes(email)) {
      setEmailError(t('members.invites.modal.errors.duplicate'))
      return false
    }

    const domainCheck = validateEmailDomain(email)
    if (!domainCheck.isValid && domainCheck.suggestion) {
      setEmailSuggestion(domainCheck.suggestion)
      return false
    }

    setEmails((prev) => [...prev, email])
    setEmailError(null)
    setEmailSuggestion(null)
    return true
  }, [emails, t])

  const removeEmail = (index: number) => {
    setEmails((prev) => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault()
      if (emailInput.trim()) {
        if (addEmail(emailInput)) {
          setEmailInput('')
        }
      }
    } else if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      removeEmail(emails.length - 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text')
    const pastedEmails = pasted.split(/[,;\s\n]+/).filter(Boolean)
    if (pastedEmails.length > 1) {
      e.preventDefault()
      let addedCount = 0
      for (const raw of pastedEmails) {
        if (addEmail(raw)) addedCount++
      }
      if (addedCount > 0) setEmailInput('')
    }
  }

  const applySuggestion = () => {
    if (emailSuggestion) {
      setEmailInput(emailSuggestion)
      setEmailSuggestion(null)
      setEmailError(null)
    }
  }

  const handleSendEmails = async () => {
    if (emailInput.trim()) {
      if (!addEmail(emailInput)) return
      setEmailInput('')
    }

    const toSend = emailInput.trim()
      ? [...emails, emailInput.trim().toLowerCase()]
      : emails

    if (toSend.length === 0) {
      setEmailError(t('members.invites.modal.errors.needAtLeastOne'))
      return
    }

    setIsSubmitting(true)
    const errors: Array<string> = []
    let successCount = 0

    for (const email of toSend) {
      try {
        await createInvite(workspaceSlug, {
          email,
          role: 'member',
          expirationDays: 7,
        })
        successCount++
      } catch (error) {
        const msg = error instanceof Error ? error.message : t('members.invites.modal.errors.failedSend')
        errors.push(`${email}: ${msg}`)
      }
    }

    setIsSubmitting(false)

    if (errors.length > 0) {
      toast.error(errors.join('\n'))
    }

    if (successCount > 0) {
      toast.success(
        successCount === 1
          ? t('members.invites.modal.invitationSent')
          : t('members.invites.modal.invitationsSent', { count: successCount }),
      )
      onInviteSent?.()
      setEmails([])
      setEmailInput('')
      setView('email')
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setView('email')
      setEmails([])
      setEmailInput('')
      setEmailError(null)
      setEmailSuggestion(null)
      setCopied(false)
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            {t('members.invites.modal.title', { workspaceName })}
          </DialogTitle>
        </DialogHeader>

        {view === 'link' ? (
          <div className="space-y-5 mt-2">
            {/* Shareable Link */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('members.invites.modal.shareableLink')}</Label>
              <Input
                readOnly
                value={loadingLink ? t('members.invites.modal.loadingLink') : (inviteUrl ?? '')}
                className="text-sm"
              />
            </div>

            {/* Copy Link Button */}
            <Button
              type="button"
              onClick={handleCopyLink}
              disabled={loadingLink || !inviteUrl}
              className="w-full"
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {t('members.invites.modal.copied')}
                </>
              ) : (
                <>
                  <Link className="mr-2 h-4 w-4" />
                  {t('members.invites.modal.copyLink')}
                </>
              )}
            </Button>

            {/* Switch to email */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setView('email')}
                className="flex items-center justify-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('members.invites.modal.inviteViaEmail')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 mt-2">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                {t('members.invites.modal.emailLabel')}
              </Label>
              <div
                className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 min-h-[42px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
              >
                {emails.map((email, index) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      title={t('members.invites.modal.removeAria', { email })}
                      onClick={() => removeEmail(index)}
                      className="text-muted-foreground hover:text-foreground"
                      disabled={isSubmitting}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  id="email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value)
                    setEmailError(null)
                    setEmailSuggestion(null)
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onBlur={() => {
                    if (emailInput.trim()) {
                      if (addEmail(emailInput)) setEmailInput('')
                    }
                  }}
                  placeholder={emails.length === 0 ? t('members.invites.modal.emailPlaceholder') : ''}
                  className="flex-1 min-w-[200px] bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
                  disabled={isSubmitting}
                />
              </div>
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
              {emailSuggestion && (
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="text-sm text-primary hover:underline"
                >
                  {t('members.invites.modal.didYouMean', { suggestion: emailSuggestion })}
                </button>
              )}
              <p className="text-xs text-muted-foreground">
                {t('members.invites.modal.multipleHint')}
              </p>
            </div>

            {/* Send Button */}
            <Button
              type="button"
              onClick={handleSendEmails}
              disabled={isSubmitting || (emails.length === 0 && !emailInput.trim())}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('members.invites.modal.sending')}
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  {t('members.invites.modal.send', { count: emails.length > 1 ? emails.length : 1 })}
                </>
              )}
            </Button>

            {/* Back to link view */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setView('link')}
                className="flex items-center justify-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('members.invites.modal.inviteViaLink')}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
